import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Skeleton,
  Stack,
  TextField,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InboxIcon from "@mui/icons-material/Inbox";
import { paymentsApi } from "../Components/paymentsApi";
import { GlobalSearchBar } from "../Components/ERPList";
import { toast } from "react-toastify";
import { CurrencyContext } from "../Contexts/CurrencyContext";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import listStyles from "./AllEnquiries.module.scss";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";

const LIST_FILTERS = [
  { value: "all", label: "All plans" },
  { value: "outstanding", label: "Outstanding" },
  { value: "overdue", label: "Overdue" },
  { value: "upcoming", label: "Upcoming" },
  { value: "paid", label: "Paid" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
  { value: "completed", label: "Completed" },
  { value: "paid", label: "Paid" },
];

const EMPTY_FILTERS = {
  list: "outstanding",
  enquiry_id: "",
  student_name: "",
  course: "",
  status: "",
  due_from: "",
  due_to: "",
};

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  height: "100%",
}));

function statusChipColor(status) {
  const s = (status || "").toLowerCase();
  if (s === "overdue") return "error";
  if (s === "paid" || s === "completed") return "success";
  if (s === "pending") return "warning";
  return "default";
}

export default function AccountsPaymentsPage() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const navigate = useNavigate();
  const { formatCurrency } = useContext(CurrencyContext);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [filterOpen, setFilterOpen] = useState(true);
  const [globalSearch, setGlobalSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const debouncedFilters = useDebouncedValue(filters, 400);

  const hasActiveFilters = useMemo(() => {
    return (
      Boolean(debouncedSearch.trim()) ||
      debouncedFilters.list !== "outstanding" ||
      Boolean(debouncedFilters.enquiry_id) ||
      Boolean(debouncedFilters.student_name) ||
      Boolean(debouncedFilters.course) ||
      Boolean(debouncedFilters.status) ||
      Boolean(debouncedFilters.due_from) ||
      Boolean(debouncedFilters.due_to)
    );
  }, [debouncedSearch, debouncedFilters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const metricsRes = await paymentsApi.metrics();
        if (!cancelled) setMetrics(metricsRes ?? null);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.message || "Failed to load metrics.");
          setMetrics(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedFilters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const listRes = await paymentsApi.accountsList(debouncedFilters.list || "outstanding", {
          page,
          page_size: rowsPerPage,
          search: debouncedSearch.trim() || undefined,
          enquiry_id: debouncedFilters.enquiry_id.trim() || undefined,
          student_name: debouncedFilters.student_name.trim() || undefined,
          course: debouncedFilters.course.trim() || undefined,
          status: debouncedFilters.status || undefined,
          due_from: debouncedFilters.due_from || undefined,
          due_to: debouncedFilters.due_to || undefined,
        });
        if (cancelled) return;
        const t = Number(listRes?.total) || 0;
        const it = listRes?.items ?? [];
        if (t > 0 && rowsPerPage > 0) {
          const maxPage = Math.max(1, Math.ceil(t / rowsPerPage));
          if (page > maxPage) {
            setListTotal(t);
            setPage(maxPage);
            return;
          }
        }
        setListTotal(t);
        setItems(it);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.message || "Failed to load payments.");
          setItems([]);
          setListTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, debouncedFilters, page, rowsPerPage]);

  const setFilterField = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setGlobalSearch("");
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={listStyles.root}>
        <Paper elevation={0} className={listStyles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={listStyles.pageTitle}>
                  Accounts – Payments
                </Typography>
                <Typography variant="body2" className={listStyles.pageSubtitle} sx={{ mt: 0.5 }}>
                  View and manage payment plans and outstanding fees.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {metrics && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard variant="outlined">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Fees Due
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(metrics.total_fees_due)}
                  </Typography>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard variant="outlined">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Collected
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "success.main" }}>
                    {formatCurrency(metrics.total_collected)}
                  </Typography>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard variant="outlined">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Outstanding
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "warning.main" }}>
                    {formatCurrency(metrics.outstanding_fees)}
                  </Typography>
                </MetricCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard variant="outlined">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Overdue Students
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "error.main" }}>
                    {metrics.overdue_students_count ?? 0}
                  </Typography>
                </MetricCard>
              </Grid>
            </Grid>
          )}

          <Box className={listStyles.header}>
            <GlobalSearchBar
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search name, enquiry ID, email, phone, course…"
            />
            <Box className={listStyles.headerActions}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterOpen((v) => !v)}
                variant="outlined"
                size="small"
              >
                {filterOpen ? "Hide Filters" : "Show Filters"}
              </Button>
              {hasActiveFilters && (
                <Button
                  startIcon={<RestartAltIcon />}
                  onClick={resetFilters}
                  variant="outlined"
                  size="small"
                  color="secondary"
                >
                  Reset
                </Button>
              )}
            </Box>
          </Box>

          {filterOpen ? (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="payments-list-filter-label">List</InputLabel>
                      <Select
                        labelId="payments-list-filter-label"
                        label="List"
                        value={filters.list}
                        onChange={(e) => setFilterField("list", e.target.value)}
                      >
                        {LIST_FILTERS.map((f) => (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Enquiry ID"
                      value={filters.enquiry_id}
                      onChange={(e) => setFilterField("enquiry_id", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Student name"
                      value={filters.student_name}
                      onChange={(e) => setFilterField("student_name", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Course"
                      value={filters.course}
                      onChange={(e) => setFilterField("course", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="payments-status-filter-label">Status</InputLabel>
                      <Select
                        labelId="payments-status-filter-label"
                        label="Status"
                        value={filters.status}
                        onChange={(e) => setFilterField("status", e.target.value)}
                      >
                        {STATUS_OPTIONS.map((f) => (
                          <MenuItem key={f.value || "any"} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Due from"
                      InputLabelProps={{ shrink: true }}
                      value={filters.due_from}
                      onChange={(e) => setFilterField("due_from", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Due to"
                      InputLabelProps={{ shrink: true }}
                      value={filters.due_to}
                      onChange={(e) => setFilterField("due_to", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </FilterPaper>
            </Fade>
          ) : null}

          {loading ? (
            <Box className={listStyles.loadingContainer}>
              <Box sx={{ width: "100%" }}>
                <Skeleton variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
              </Box>
            </Box>
          ) : listTotal === 0 ? (
            <Box className={listStyles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No records match
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try clearing search or adjusting filters.
              </Typography>
            </Box>
          ) : (
            <Paper variant="outlined" className={listStyles.tableContainer}>
              <Table stickyHeader size="small" className={listStyles.enquiriesTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Course</TableCell>
                    <TableCell>Total Fee</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Outstanding</TableCell>
                    <TableCell>Next Due Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      key={`${row.enquiry_id}-${row.plan_id}`}
                      hover
                      className={listStyles.tableRow}
                      onClick={() => navigate(`/accounts/payment-plan/${row.plan_id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" className={listStyles.primaryText} noWrap>
                          {row.student_name}
                        </Typography>
                        {(row.email || row.phone_number) && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {[row.email, row.phone_number].filter(Boolean).join(" · ")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={listStyles.secondaryText} noWrap title={row.course}>
                          {row.course ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatCurrency(row.total_fee)}</TableCell>
                      <TableCell>{formatCurrency(row.paid)}</TableCell>
                      <TableCell>
                        {row.outstanding > 0 ? (
                          <Chip
                            size="small"
                            label={formatCurrency(row.outstanding)}
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          formatCurrency(row.outstanding)
                        )}
                      </TableCell>
                      <TableCell>{row.next_due_date ?? "—"}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Chip
                          size="small"
                          label={row.status || "—"}
                          color={statusChipColor(row.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/accounts/payment-plan/${row.plan_id}`)}
                        >
                          View Plan
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      colSpan={8}
                      count={listTotal}
                      rowsPerPage={rowsPerPage}
                      page={page - 1}
                      onPageChange={(_, newPage) => setPage(newPage + 1)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(1);
                      }}
                      labelRowsPerPage="Rows"
                      sx={{ borderBottom: "none" }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Paper>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
